import React, { useState } from "react";
import { useNavigate } from "react-router";

import { DefaultButton, DropdownField, Form } from "@bps/fluent-ui";
import { ValidationSchema, Validator } from "@bps/validator";
import {
  BulkActionResponse,
  TargetConfigurationType
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useBulkCommandDispatch } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { SelectSoftwarePackageFormValues } from "../../../rings/rollout-package/add/selectSoftwarePackageUtils";
import { BulkActionType, BulkSendCommandFormValues } from "../../formTypes";
import { BulkActionFormWrapper } from "../BulkActionFormWrapper";
import { SelectSoftwarePackageBulkDialog } from "./SelectSoftwarePackageBulkDialog";
import { SelectSoftwarePackageCommand } from "./SelectSoftwarePackageCommand";
import { SoftwarePackageDetails } from "./SoftwarePackageDetails";

const validator = new Validator<BulkSendCommandFormValues>();

const schema: ValidationSchema<BulkSendCommandFormValues> = {
  selectorId: validator.string().required({ message: "Selector is required" }),
  actionType: validator
    .string()
    .required({ message: "Action Type is required" })
};

export const SendBulkCommandForm: React.FC = () => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [softwarePackage, setSelectedSoftwarePackage] = useState<
    SelectSoftwarePackageFormValues
  >();

  const navigate = useNavigate();
  const [responseMessage, setResponseMessage] = useState<
    BulkActionResponse | undefined
  >();

  const { mutateAsync: dispatchBulkCommands } = useBulkCommandDispatch();

  const onSubmit = async (formValues: BulkSendCommandFormValues) => {
    const {
      selectorId,
      smartDeviceCommandMessage,
      configurationType
    } = formValues;
    if (!selectorId) return;

    if (smartDeviceCommandMessage === undefined) return;

    // Submit request for bulk action
    const response = await dispatchBulkCommands({
      selectorId,
      smartDeviceCommandMessage,
      targetConfigurationType: configurationType
    });

    setResponseMessage(response);
  };

  const targetConfigurationOptions =
    Object.values(TargetConfigurationType)?.map(key => {
      return { key, text: key };
    }) ?? [];

  return (
    <Form.Provider<BulkSendCommandFormValues>
      onSubmit={onSubmit}
      initialValues={{
        actionType: BulkActionType.SendCommand,
        selectorId: undefined,
        smartDeviceCommandMessage: undefined,
        configurationType: TargetConfigurationType.Reported // Reported state by default
      }}
      validate={values => validator.validate(values, schema)}
    >
      {(state, actions) => {
        return (
          <>
            <form
              onSubmit={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              name="Bulk Actions Form"
            >
              <BulkActionFormWrapper
                responseMessage={responseMessage}
                showSubmit={false}
              >
                <DropdownField
                  label="Target Configuration Type"
                  name="configurationType"
                  required
                  options={targetConfigurationOptions}
                  styles={{ root: { width: "30%" } }}
                  withNoEmptyOption
                />
                {softwarePackage === undefined ? (
                  <DefaultButton
                    iconProps={{ iconName: "Add" }}
                    onClick={() => {
                      setShowDialog(true);
                      navigate("/system/plt/bulk-actions");
                    }}
                    text="Select Software Package"
                    styles={{ root: { width: "30%" } }}
                  />
                ) : (
                  <SoftwarePackageDetails
                    setShowDialog={setShowDialog}
                    softwarePackage={softwarePackage}
                    setSelectedSoftwarePackage={setSelectedSoftwarePackage}
                  />
                )}
              </BulkActionFormWrapper>
            </form>

            {/* The generated json form is rendered outside the bulk form wrapper as html does not allow nested forms.  */}
            {softwarePackage !== undefined && (
              <SelectSoftwarePackageCommand
                softwarePackage={softwarePackage}
                formActions={actions}
              />
            )}

            {/* Dialog */}
            {showDialog && (
              <SelectSoftwarePackageBulkDialog
                onDismiss={() => setShowDialog(false)}
                setSelectedSoftwarePackage={setSelectedSoftwarePackage}
              />
            )}
          </>
        );
      }}
    </Form.Provider>
  );
};
